// WbProductDetailPage — /weighbridge/products/:enc_id (and /new).
//
// Phase C3 Group 4. Single-page create + edit form. Delete sits on the
// same page, behind a ConfirmModal. The server's `default_general_public_price`
// is surfaced as "Default unit price" in the UI — that's the seller-facing
// name; "general public" is a server-side concept that distinguishes the
// catalogue price from contract / price-band overrides resolved at ticket
// time. Form fields match server/src/modules/wb/products/types.ts (NOT the
// original C3 prompt — that file's `category` / `pricing_unit` / `waste_type`
// columns do not exist on the schema).

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateProduct,
  useDeleteProduct,
  useUpdateProduct,
  useWbProduct,
  useWbVatRates,
  type CreateProductInput,
  type DirectionDefault,
  type ProductMode,
  type UnitOfMeasure,
} from '../hooks/useWbProducts';
import { useWbProductGroups } from '../hooks/useWbProductGroups';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbProductDetailPage.module.css';

// EWC code: 6 numeric digits. Server enforces the same regex; we mirror
// it client-side to avoid round-trips on obvious typos ("abc", "12345").
const EWC_REGEX = /^\d{6}$/;

const MODE_OPTIONS: ReadonlyArray<{ value: ProductMode; label: string }> = [
  { value: 'weight', label: 'Weight' },
  { value: 'unit', label: 'Unit' },
];

const UNIT_OPTIONS: ReadonlyArray<{ value: UnitOfMeasure; label: string }> = [
  { value: 'tonne', label: 'Per tonne' },
  { value: 'kg', label: 'Per kg' },
  { value: 'each', label: 'Each' },
  { value: 'm3', label: 'Per m³' },
];

const DIRECTION_OPTIONS: ReadonlyArray<{
  value: DirectionDefault;
  label: string;
}> = [
  { value: 'inwards', label: 'Inwards' },
  { value: 'outwards', label: 'Outwards' },
  { value: 'both', label: 'Both' },
];

interface FormState {
  name: string;
  code: string;
  // Group is tracked by its stable `code` (not enc_id) because entity enc_ids
  // re-encrypt with a random IV per response — the product DTO's group enc_id
  // won't match the group-list enc_id. We translate code → enc_id on submit.
  group_code: string; // '' === no group
  description: string;
  mode: ProductMode;
  unit_of_measure: UnitOfMeasure;
  default_general_public_price: string; // text input — empty handled by validate
  vat_rate_id: string; // '' === no VAT
  ewc_code: string;
  is_hazardous: boolean;
  direction_default: DirectionDefault;
  stock_tracked: boolean;
  stock_threshold_low: string;
  stock_threshold_high: string;
  stock_threshold_unit: UnitOfMeasure;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  code: '',
  group_code: '',
  description: '',
  mode: 'weight',
  unit_of_measure: 'tonne',
  default_general_public_price: '',
  vat_rate_id: '',
  ewc_code: '',
  is_hazardous: false,
  direction_default: 'both',
  stock_tracked: false,
  stock_threshold_low: '',
  stock_threshold_high: '',
  stock_threshold_unit: 'tonne',
  is_active: true,
};

export function WbProductDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const productQuery = useWbProduct(isNew ? undefined : enc_id);
  const createMut = useCreateProduct();
  const updateMut = useUpdateProduct(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteProduct(isNew ? '' : (enc_id as string));
  const vatRatesQuery = useWbVatRates();
  const vatRates = vatRatesQuery.data?.vat_rates ?? [];
  const groupsQuery = useWbProductGroups();
  const groups = groupsQuery.data?.groups ?? [];

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    const p = productQuery.data?.product;
    if (!p) return;
    setForm({
      name: p.name,
      code: p.code,
      group_code: p.group?.code ?? '',
      description: p.description ?? '',
      mode: p.mode,
      unit_of_measure: p.unit_of_measure,
      default_general_public_price: String(p.default_general_public_price),
      vat_rate_id: p.vat_rate?.enc_id ?? '',
      ewc_code: p.ewc_code ?? '',
      is_hazardous: p.is_hazardous,
      direction_default: p.direction_default,
      stock_tracked: p.stock_tracked,
      stock_threshold_low:
        p.stock_threshold_low !== null ? String(p.stock_threshold_low) : '',
      stock_threshold_high:
        p.stock_threshold_high !== null ? String(p.stock_threshold_high) : '',
      // Server allows any string for stock_threshold_unit but our schema
      // narrows it to the four enum values. Fall back to the product's
      // unit_of_measure if the value isn't one of those (defensive).
      stock_threshold_unit:
        p.stock_threshold_unit === 'tonne' ||
        p.stock_threshold_unit === 'kg' ||
        p.stock_threshold_unit === 'each' ||
        p.stock_threshold_unit === 'm3'
          ? (p.stock_threshold_unit as UnitOfMeasure)
          : p.unit_of_measure,
      is_active: p.is_active,
    });
  }, [isNew, productQuery.data]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (form.name.length > 200) errs['name'] = 'Name must be 200 characters or fewer.';
    if (!form.code.trim()) errs['code'] = 'Code is required.';
    if (form.code.length > 50) errs['code'] = 'Code must be 50 characters or fewer.';
    if (form.description.length > 2000) {
      errs['description'] = 'Description must be 2000 characters or fewer.';
    }
    if (form.default_general_public_price.trim() === '') {
      errs['default_general_public_price'] = 'Default unit price is required.';
    } else {
      const n = Number(form.default_general_public_price);
      if (!Number.isFinite(n) || n < 0) {
        errs['default_general_public_price'] =
          'Default unit price must be a non-negative number.';
      }
    }
    const ewcTrim = form.ewc_code.trim();
    if (ewcTrim !== '' && !EWC_REGEX.test(ewcTrim)) {
      errs['ewc_code'] = 'EWC code must be exactly 6 digits.';
    }
    if (form.stock_tracked) {
      if (form.stock_threshold_low !== '') {
        const lo = Number(form.stock_threshold_low);
        if (!Number.isFinite(lo) || lo < 0) {
          errs['stock_threshold_low'] =
            'Low threshold must be a non-negative number.';
        }
      }
      if (form.stock_threshold_high !== '') {
        const hi = Number(form.stock_threshold_high);
        if (!Number.isFinite(hi) || hi < 0) {
          errs['stock_threshold_high'] =
            'High threshold must be a non-negative number.';
        }
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildBody(): CreateProductInput {
    const trimmedDescription = form.description.trim();
    const trimmedEwc = form.ewc_code.trim();
    const body: CreateProductInput = {
      name: form.name.trim(),
      code: form.code.trim(),
      description: trimmedDescription === '' ? null : trimmedDescription,
      mode: form.mode,
      unit_of_measure: form.unit_of_measure,
      default_general_public_price: Number(form.default_general_public_price),
      vat_rate_id: form.vat_rate_id === '' ? null : form.vat_rate_id,
      ewc_code: trimmedEwc === '' ? null : trimmedEwc,
      is_hazardous: form.is_hazardous,
      direction_default: form.direction_default,
      stock_tracked: form.stock_tracked,
      is_active: form.is_active,
    };
    // Translate the stable group code back to its enc_id. '' clears the group.
    // A set code with no match in the (loaded) list is left untouched (omit) so
    // an unloaded list can't silently strip an existing assignment.
    if (form.group_code === '') {
      body.group_id = null;
    } else {
      const grp = groups.find((g) => g.code === form.group_code);
      if (grp) body.group_id = grp.enc_id;
    }
    if (form.stock_tracked) {
      body.stock_threshold_low =
        form.stock_threshold_low === ''
          ? null
          : Number(form.stock_threshold_low);
      body.stock_threshold_high =
        form.stock_threshold_high === ''
          ? null
          : Number(form.stock_threshold_high);
      body.stock_threshold_unit = form.stock_threshold_unit;
    } else {
      // Clear thresholds when stock-tracking is off so a previously-set
      // value doesn't leak back in on update.
      body.stock_threshold_low = null;
      body.stock_threshold_high = null;
      body.stock_threshold_unit = null;
    }
    return body;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    const body = buildBody();
    if (isNew) {
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/products'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/products'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        navigate('/weighbridge/products');
      },
      onError: (err) => {
        setConfirmDeleteOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const isLoadingExisting = !isNew && productQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Product' : form.name || 'Edit Product'}
        subtitle={
          isNew ? 'Add a new product to the catalogue' : 'Edit product details'
        }
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/products')}
          >
            Back to Products
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading product" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            {/* -- Identity ----------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Identity</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Name</span>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                  {fieldErrors['name'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['name']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Code</span>
                  <input
                    type="text"
                    required
                    maxLength={50}
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, code: e.target.value }))
                    }
                    onBlur={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                  {fieldErrors['code'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['code']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Group</span>
                  <select
                    value={form.group_code}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, group_code: e.target.value }))
                    }
                    disabled={groupsQuery.isLoading}
                  >
                    <option value="">— No group —</option>
                    {groups
                      // Selectable: non-excluded groups, plus the currently
                      // assigned one (so an existing excluded assignment shows
                      // and isn't silently dropped on save).
                      .filter((g) => !g.is_excluded || g.code === form.group_code)
                      .map((g) => (
                        <option key={g.code} value={g.code}>
                          {g.name}
                          {g.is_global ? ' (global)' : ''}
                        </option>
                      ))}
                  </select>
                </label>
              </div>

              <label className={styles.field}>
                <span>Description</span>
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                />
                {fieldErrors['description'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['description']}
                  </span>
                ) : null}
              </label>
            </div>

            {/* -- Pricing & Units ---------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Pricing &amp; Units</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Mode</span>
                  <select
                    value={form.mode}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        mode: e.target.value as ProductMode,
                      }))
                    }
                  >
                    {MODE_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={styles.field}>
                  <span>Unit of measure</span>
                  <select
                    value={form.unit_of_measure}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        unit_of_measure: e.target.value as UnitOfMeasure,
                      }))
                    }
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={styles.field}>
                  <span>Default unit price (£)</span>
                  <input
                    type="number"
                    required
                    min={0}
                    step="0.01"
                    value={form.default_general_public_price}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        default_general_public_price: e.target.value,
                      }))
                    }
                  />
                  {fieldErrors['default_general_public_price'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['default_general_public_price']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>VAT rate</span>
                  <select
                    value={form.vat_rate_id}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        vat_rate_id: e.target.value,
                      }))
                    }
                    disabled={vatRatesQuery.isLoading}
                  >
                    <option value="">— No VAT —</option>
                    {vatRates.map((r) => (
                      <option key={r.enc_id} value={r.enc_id}>
                        {r.name} ({r.percentage}%)
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {/* -- Waste & Compliance ------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Waste &amp; Compliance</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>EWC code (6 digits)</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={form.ewc_code}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        ewc_code: e.target.value,
                      }))
                    }
                    placeholder="e.g. 170101"
                  />
                  <span className={styles.fieldHelp}>
                    Environment Agency Waste Classification code (numeric, no
                    spaces).
                  </span>
                  {fieldErrors['ewc_code'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['ewc_code']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Default direction</span>
                  <select
                    value={form.direction_default}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        direction_default: e.target.value as DirectionDefault,
                      }))
                    }
                  >
                    {DIRECTION_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div>
                <label className={styles.toggleRow}>
                  <input
                    type="checkbox"
                    checked={form.is_hazardous}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_hazardous: e.target.checked,
                      }))
                    }
                  />
                  <span>Hazardous waste</span>
                </label>
                <span className={styles.toggleHelp}>
                  Hazardous tickets will warn if no compliance docs are
                  attached.
                </span>
              </div>
            </div>

            {/* -- Stock Tracking ----------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Stock Tracking</h3>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.stock_tracked}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      stock_tracked: e.target.checked,
                      // Seed the threshold unit from the chosen UoM the
                      // first time stock-tracking is enabled (UX nicety).
                      stock_threshold_unit: e.target.checked
                        ? prev.unit_of_measure
                        : prev.stock_threshold_unit,
                    }))
                  }
                />
                <span>Track stock movements for this product</span>
              </label>

              {form.stock_tracked ? (
                <div className={styles.grid}>
                  <label className={styles.field}>
                    <span>Low threshold</span>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={form.stock_threshold_low}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          stock_threshold_low: e.target.value,
                        }))
                      }
                      placeholder="Optional"
                    />
                    {fieldErrors['stock_threshold_low'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['stock_threshold_low']}
                      </span>
                    ) : null}
                  </label>

                  <label className={styles.field}>
                    <span>High threshold</span>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={form.stock_threshold_high}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          stock_threshold_high: e.target.value,
                        }))
                      }
                      placeholder="Optional"
                    />
                    {fieldErrors['stock_threshold_high'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['stock_threshold_high']}
                      </span>
                    ) : null}
                  </label>

                  <label className={styles.field}>
                    <span>Threshold unit</span>
                    <select
                      value={form.stock_threshold_unit}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          stock_threshold_unit: e.target.value as UnitOfMeasure,
                        }))
                      }
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}
            </div>

            {/* -- Status ------------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Status</h3>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
                <span>Active</span>
              </label>
            </div>
          </Card>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/products')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Product' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete product?"
        message={
          <p>
            Delete <strong>{form.name || 'this product'}</strong>? This cannot
            be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
